import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FolderOpen, Plus, Clock, CheckCircle2, XCircle, Download, Eye, Calendar, Mail, User, File, AlertCircle, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, CalendarPlus, Link2, Check } from 'lucide-react';
import { fileRequestApi, uploadApi, authApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

export function DashboardPage() {
  const [fileRequests, setFileRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [viewingFiles, setViewingFiles] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    // Check for notification from navigation state
    if (location.state?.notification) {
      setNotification(location.state.notification);
      // Clear the notification from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is authenticated
        if (!authApi.isAuthenticated()) {
          navigate('/login');
          return;
        }

        // Get current user
        const userData = await authApi.getCurrentUser();
        setCurrentUser(userData);

        // Fetch file requests
        const data = await fileRequestApi.getByUser(userData.email);
        setFileRequests(data);
      } catch (err) {
        if (err.response?.status === 401) {
          // Token expired or invalid
          authApi.logout();
          navigate('/login');
        } else {
          setError('Failed to load file requests');
          console.error('Dashboard error:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-security-600" />;
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-orange-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'expired':
        return 'Expired';
      default:
        return 'Pending';
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-security-100 text-security-800`;
      case 'expired':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-orange-100 text-orange-800`;
    }
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''} left`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} left`;
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes} min left`;
    }
  };

  const handleViewFiles = async (requestId) => {
    setFilesLoading(true);
    setViewingFiles(requestId);
    try {
      const filesData = await fileRequestApi.getFiles(requestId);
      setFiles(filesData);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await uploadApi.download(fileId);

      // Create blob link and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download file');
    }
  };

  const handleToggleReusable = async (requestId, currentValue) => {
    try {
      const result = await fileRequestApi.toggleReusable(requestId, !currentValue);
      // Update the local state
      setFileRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, is_reusable: result.is_reusable, status: result.status }
          : req
      ));
    } catch (err) {
      console.error('Failed to toggle reusable status:', err);
      setError(err.response?.data?.detail || 'Failed to update reusable status');
    }
  };

  const [copiedLink, setCopiedLink] = useState(null);
  const [extendingRequest, setExtendingRequest] = useState(null);
  const [extendHours, setExtendHours] = useState(72);

  const handleCopyLink = async (uploadUrl, requestId) => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      setCopiedLink(requestId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleExtendExpiration = async (requestId) => {
    try {
      const result = await fileRequestApi.extendExpiration(requestId, extendHours);
      // Update the local state
      setFileRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, expires_at: result.expires_at, status: result.status }
          : req
      ));
      setExtendingRequest(null);
      setExtendHours(72);
    } catch (err) {
      console.error('Failed to extend expiration:', err);
      setError(err.response?.data?.detail || 'Failed to extend expiration');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sorting functions
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (field !== sortField) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-4 h-4 text-primary-600" />
      : <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  // Filtered and sorted requests
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...fileRequests];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.subject.toLowerCase().includes(searchLower) ||
        request.recipient_name.toLowerCase().includes(searchLower) ||
        request.recipient_email.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'created_at' || sortField === 'expires_at') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [fileRequests, searchTerm, statusFilter, sortField, sortDirection]);

  // Pagination
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRequests, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedRequests.length / itemsPerPage);

  const Pagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx + 1}
                  onClick={() => setCurrentPage(idx + 1)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    currentPage === idx + 1
                      ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="spinner w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your file requests...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
              <p className="text-lg text-gray-600">
                Manage your secure file transfer requests
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Link to="/create-request" className="btn-primary flex items-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>New Request</span>
              </Link>
            </div>
          </div>
        </div>

        {notification && (
          <div className="mb-6 p-4 bg-security-50 border border-security-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-security-600 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-security-800">
                  {notification.title}
                </h3>
                <p className="text-sm text-security-700 mt-1">
                  {notification.message}
                </p>
                {notification.uploadUrl && (
                  <div className="mt-3 text-xs">
                    <p className="text-security-600">
                      <strong>Upload URL:</strong>{' '}
                      <span className="font-mono bg-security-100 px-2 py-1 rounded">
                        {notification.uploadUrl}
                      </span>
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-security-400 hover:text-security-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" aria-hidden="true" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FolderOpen className="w-8 h-8 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {fileRequests.length}
                  </p>
                  <p className="text-sm text-gray-600">Total Requests</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-8 h-8 text-security-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {fileRequests.filter(r => r.status === 'completed').length}
                  </p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {fileRequests.filter(r => r.status === 'pending').length}
                  </p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Requests List */}
        <div className="card">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">File Requests</h2>

            {/* Filters and Search */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by subject or recipient..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>

            {/* Sort Controls */}
            <div className="mb-4 flex items-center space-x-4 text-sm">
              <span className="text-gray-600">Sort by:</span>
              <button
                onClick={() => handleSort('created_at')}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Created</span>
                <SortIcon field="created_at" />
              </button>
              <button
                onClick={() => handleSort('subject')}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Subject</span>
                <SortIcon field="subject" />
              </button>
              <button
                onClick={() => handleSort('status')}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Status</span>
                <SortIcon field="status" />
              </button>
              <button
                onClick={() => handleSort('expires_at')}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Expires</span>
                <SortIcon field="expires_at" />
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Showing {filteredAndSortedRequests.length} request{filteredAndSortedRequests.length !== 1 ? 's' : ''}
            </div>

            {filteredAndSortedRequests.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No file requests yet</h3>
                <p className="text-gray-600 mb-6">Create your first secure file transfer request to get started.</p>
                <Link to="/create-request" className="btn-primary">
                  Create First Request
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(request.status)}
                          <h3 className="text-lg font-semibold text-gray-900">
                            {request.subject}
                          </h3>
                          <span className={getStatusBadge(request.status)}>
                            {getStatusText(request.status)}
                          </span>
                          {request.is_reusable && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Reusable
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <User className="w-4 h-4" />
                            <span>{request.recipient_name}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{request.recipient_email}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Created {formatDate(request.created_at)}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-sm">
                            <Clock className="w-4 h-4" />
                            <span className={
                              request.status === 'expired' 
                                ? 'text-red-600' 
                                : 'text-orange-600'
                            }>
                              {request.status === 'expired' 
                                ? 'Expired' 
                                : getTimeRemaining(request.expires_at)
                              }
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <FolderOpen className="w-4 h-4" />
                            <span>{request.files_count} file{request.files_count !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Copy Upload Link */}
                        {request.upload_url && (
                          <button
                            onClick={() => handleCopyLink(request.upload_url, request.id)}
                            className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                              copiedLink === request.id
                                ? 'border-security-300 text-security-700 bg-security-50'
                                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                            title="Copy upload link"
                          >
                            {copiedLink === request.id ? (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Link2 className="w-4 h-4 mr-2" />
                                Copy Link
                              </>
                            )}
                          </button>
                        )}

                        {/* Extend Expiration - show for reusable (any status) or single-use (pending/expired) */}
                        {(request.is_reusable || request.status !== 'completed') && (
                          <button
                            onClick={() => setExtendingRequest(request.id)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            title="Extend expiration"
                          >
                            <CalendarPlus className="w-4 h-4 mr-2" />
                            Extend
                          </button>
                        )}

                        {/* Toggle Reusable - only show for non-expired requests */}
                        {request.status !== 'expired' && (
                          <button
                            onClick={() => handleToggleReusable(request.id, request.is_reusable)}
                            className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                              request.is_reusable
                                ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                            title={request.is_reusable ? 'Make single-use' : 'Make reusable'}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {request.is_reusable ? 'Reusable' : 'Single-use'}
                          </button>
                        )}

                        {(request.status === 'completed' || request.is_reusable) && request.files_count > 0 && (
                          <button
                            onClick={() => handleViewFiles(request.id)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Files
                          </button>
                        )}

                        {(request.status === 'completed' || request.is_reusable) && request.files_count > 0 && (
                          <button
                            onClick={() => handleViewFiles(request.id)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination />
          </div>
        </div>

        {/* Files Modal */}
        {viewingFiles && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Files for Request
                  </h3>
                  <button
                    onClick={() => {
                      setViewingFiles(null);
                      setFiles([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {filesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-3"></div>
                    <span className="text-gray-600">Loading files...</span>
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No files found</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <File className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {file.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)} • {formatDate(file.uploaded_at)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadFile(file.id, file.filename)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Extend Expiration Modal */}
        {extendingRequest && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Extend Expiration
                  </h3>
                  <button
                    onClick={() => {
                      setExtendingRequest(null);
                      setExtendHours(72);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extend by:
                  </label>
                  <select
                    value={extendHours}
                    onChange={(e) => setExtendHours(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={24}>24 hours</option>
                    <option value={48}>2 days</option>
                    <option value={72}>3 days</option>
                    <option value={168}>1 week</option>
                    <option value={336}>2 weeks</option>
                    <option value={720}>30 days</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    {fileRequests.find(r => r.id === extendingRequest)?.status === 'expired'
                      ? 'This link has expired. Extending will reactivate it from now.'
                      : 'Time will be added to the current expiration date.'}
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => handleExtendExpiration(extendingRequest)}
                    className="flex-1 btn-primary"
                  >
                    Extend
                  </button>
                  <button
                    onClick={() => {
                      setExtendingRequest(null);
                      setExtendHours(72);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}